import React from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import countryList from 'country-list';
import PropTypes from 'prop-types';
import { ShowLessArrowsIcon, ShowMoreArrowsIcon } from '@/assets/images';
import AddRemoveControl from '@/components/common/AddRemoveControl';
import CustomLink from '@/components/common/CustomLink';
import AddressAutocomplete from '@/components/common/form/AddressAutocomplete';
import ControlledTextField from '@/components/common/form/ControlledTextField';
import USAStateSelect from '@/components/common/USAStateSelect';
import translate from '@/lib/intl/translate';
import ConstituentField from '../constants/ConstituentField';
import LockedFormHelperTextProps from '../constants/LockedFormHelperTextProps';
import LockedSalesforceHelperText from './LockedSalesforceHelperText';

const getNewAddress = () => ({
  city: '',
  country: 'US',
  formattedAddress: '',
  gmapsAddressComponents: [],
  gmapsPlaceId: '',
  latitude: null,
  line1: '',
  line2: '',
  longitude: null,
  postalCode: '',
  state: '',
  unit: '',
});

// Maps address components from Google's Places API to our address fields.
export const mapAddressComponents = (addressComponents = []) => {
  const newAddress = {};

  addressComponents.forEach(({ shortName, types }) => {
    if (types.includes('street_number')) {
      newAddress.line1 = shortName;
    } else if (types.includes('route')) {
      newAddress.line1 = [
        newAddress.line1 ? `${newAddress.line1} ` : '',
        shortName,
      ].join('');
    } else if (
      types.includes('locality') ||
      types.includes('sublocality') ||
      types.includes('postal_town') ||
      types.includes('neighborhood')
    ) {
      newAddress.city = shortName;
    } else if (types.includes('administrative_area_level_1')) {
      newAddress.state = shortName;
    } else if (types.includes('postal_code')) {
      newAddress.postalCode = shortName;
    } else if (types.includes('country')) {
      newAddress.country = shortName;
    }
  });
  return newAddress;
};

function AddressField({ append, fields, index, locked, remove }) {
  const field = fields[index];
  const { control, setValue, watch } = useFormContext();
  const [showManualEntry, setShowManualEntry] = React.useState(
    !field.formattedAddress &&
      [
        field.line1,
        field.line2,
        field.city,
        field.postalCode,
        field.state,
        field.unit,
      ].some(Boolean),
  );

  const handleAutocomplete = (
    formattedAddress,
    { addressComponents, geometry, placeId },
  ) =>
    setValue(`${ConstituentField.ADDRESSES}.${index}`, {
      ...getNewAddress(),
      ...mapAddressComponents(addressComponents),
      latitude: geometry?.location.lat(),
      longitude: geometry?.location.lng(),
      gmapsAddressComponents: addressComponents,
      gmapsPlaceId: placeId,
      formattedAddress,
    });

  const country = watch(`${ConstituentField.ADDRESSES}.${index}.country`);

  // Focus on the first autocomplete input if the hash is `#address`. We can't
  // directly use refs for the address field here because the Google
  // autocomplete widget is assigning its own ref to the input, so
  // instead we are doing some direct DOM querying, scoped to this component.
  const { hash } = useLocation();
  const containerRef = React.useRef();
  React.useEffect(() => {
    if (index || hash !== '#address') return;
    containerRef.current?.querySelector('input')?.focus();
  }, [containerRef, hash, index]);

  return (
    <>
      <div
        className="relative col-span-full flex items-center"
        ref={containerRef}
      >
        {showManualEntry ? (
          <ControlledTextField
            className="flex-grow"
            disabled={locked}
            control={control}
            helperText={<LockedSalesforceHelperText locked={locked} />}
            fullWidth
            label={translate('STREET_ADDRESS_LABEL')}
            name={`${ConstituentField.ADDRESSES}.${index}.line1`}
            FormHelperTextProps={LockedFormHelperTextProps}
          />
        ) : (
          <AddressAutocomplete
            className="flex-grow"
            disabled={locked}
            label={translate('FORMS_ADDRESS_LABEL')}
            onChange={handleAutocomplete}
            defaultValue={field.formattedAddress}
          />
        )}
        <AddRemoveControl
          className="flex-shrink-0 pl-4 lg:absolute lg:-right-16 lg:-mr-2"
          disabled={locked}
          onAdd={() => append(getNewAddress())}
          onRemove={() => remove(index)}
          showAdd={index === fields.length - 1}
          showRemove={fields.length > 1}
        />
      </div>
      {showManualEntry && (
        <>
          <ControlledTextField
            className="col-span-full"
            control={control}
            disabled={locked}
            fullWidth
            helperText={<LockedSalesforceHelperText locked={locked} />}
            label={translate('STREET_ADDRESS_LABEL_LINE_2')}
            name={`${ConstituentField.ADDRESSES}.${index}.line2`}
            FormHelperTextProps={LockedFormHelperTextProps}
          />
          <ControlledTextField
            className="col-span-full"
            control={control}
            disabled={locked}
            helperText={<LockedSalesforceHelperText locked={locked} />}
            label={translate('APT_LABEL')}
            name={`${ConstituentField.ADDRESSES}.${index}.unit`}
            FormHelperTextProps={LockedFormHelperTextProps}
          />
          <ControlledTextField
            className="col-span-full"
            control={control}
            disabled={locked}
            helperText={<LockedSalesforceHelperText locked={locked} />}
            label={translate('CITY_LABEL')}
            name={`${ConstituentField.ADDRESSES}.${index}.city`}
            FormHelperTextProps={LockedFormHelperTextProps}
          />
          {country?.toUpperCase() === 'US' ? (
            <Controller
              control={control}
              disabled={locked}
              name={`${ConstituentField.ADDRESSES}.${index}.state`}
              render={({ field: { onChange, value } }) => (
                <USAStateSelect
                  className="col-span-full"
                  helperText={<LockedSalesforceHelperText locked={locked} />}
                  onChange={onChange}
                  value={value}
                  FormHelperTextProps={LockedFormHelperTextProps}
                />
              )}
            />
          ) : (
            <ControlledTextField
              className="col-span-full"
              disabled={locked}
              control={control}
              label={translate('STATE_LABEL')}
              helperText={<LockedSalesforceHelperText locked={locked} />}
              name={`${ConstituentField.ADDRESSES}.${index}.state`}
              FormHelperTextProps={LockedFormHelperTextProps}
            />
          )}
          <ControlledTextField
            className="lg:col-span-2"
            disabled={locked}
            control={control}
            label={translate('ZIP_LABEL')}
            helperText={<LockedSalesforceHelperText locked={locked} />}
            name={`${ConstituentField.ADDRESSES}.${index}.postalCode`}
            FormHelperTextProps={LockedFormHelperTextProps}
          />
          <FormControl className="lg:col-span-2">
            <InputLabel>{translate('COUNTRY_LABEL')}</InputLabel>
            <Controller
              control={control}
              name={`${ConstituentField.ADDRESSES}.${index}.country`}
              render={({ field: { onChange, value } }) => (
                <Select
                  disabled={locked}
                  label={translate('COUNTRY_LABEL')}
                  value={value?.toUpperCase()}
                  onChange={onChange}
                >
                  {countryList.getData().map(({ code, name }) => (
                    <MenuItem
                      value={code.toUpperCase()}
                      key={code.toUpperCase()}
                    >
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />

            {locked && (
              <FormHelperText {...LockedFormHelperTextProps}>
                <LockedSalesforceHelperText locked />
              </FormHelperText>
            )}
          </FormControl>
        </>
      )}
      <div className="col-span-full">
        <CustomLink
          component="button"
          onClick={() => {
            if (!locked) {
              setShowManualEntry(!showManualEntry);
            }
          }}
          className="flex items-center"
          type="button"
        >
          <span className="mr-2">
            {translate(
              showManualEntry ? 'SEARCH_FOR_ADDRESS' : 'ENTER_ADDRESS_MANUALLY',
            )}
          </span>
          {showManualEntry ? <ShowLessArrowsIcon /> : <ShowMoreArrowsIcon />}
        </CustomLink>
      </div>
    </>
  );
}

AddressField.propTypes = {
  append: PropTypes.func.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      city: PropTypes.string,
      country: PropTypes.string,
      formattedAddress: PropTypes.string,
      gmapsAddressComponents: PropTypes.arrayOf(PropTypes.object),
      gmapsPlaceId: PropTypes.string,
      latitude: PropTypes.number,
      line1: PropTypes.string,
      line2: PropTypes.string,
      longitude: PropTypes.number,
      postalCode: PropTypes.string,
      state: PropTypes.string,
      unit: PropTypes.string,
      id: PropTypes.string.isRequired,
    }),
  ).isRequired,
  index: PropTypes.number.isRequired,
  locked: PropTypes.bool.isRequired,
  remove: PropTypes.func.isRequired,
};

function AddressFields({ locked }) {
  const { control } = useFormContext();
  const fieldArray = useFieldArray({
    control,
    name: ConstituentField.ADDRESSES,
  });
  const { fields, append } = fieldArray;

  React.useEffect(() => {
    if (!fields.length) {
      append(getNewAddress());
    }
  }, [fields]);

  return (
    <>
      {fields.map((field, i) => (
        <AddressField
          key={field.id}
          index={i}
          locked={locked}
          {...fieldArray}
        />
      ))}
    </>
  );
}

AddressFields.propTypes = {
  locked: PropTypes.bool.isRequired,
};

export default AddressFields;
